import { CatalogProduct } from '../../types/catalog';
import { ProductInView, VariantRow, flattenCatalog } from './catalogFilters';
import { matchesTokens, tokenize } from './tokens';

export interface SearchEntry {
  row: VariantRow;
  tokens: string[];
}

export function buildSearchIndex(catalog: CatalogProduct[]): SearchEntry[] {
  return flattenCatalog(catalog).map(row => {
    const { product, variant } = row;
    const text = [
      product.name,
      product.brand?.name ?? '',
      variant.variantLabel ?? '',
      ...Object.values(variant.attributes).map(v => v.label),
    ].join(' ');
    return { row, tokens: [...new Set(tokenize(text))] };
  });
}

/** Products with a variant matching every query token, carrying those variants; exact hits first. */
export function searchCatalog(
  index: SearchEntry[],
  query: string,
  limit = 50,
): ProductInView[] {
  const queryTokens = tokenize(query);
  if (!queryTokens.length) return [];

  const byProduct = new Map<string, ProductInView & { score: number }>();
  index.forEach(entry => {
    if (!matchesTokens(entry.tokens, queryTokens)) return;
    const score = queryTokens.filter(q => entry.tokens.includes(q)).length;
    const { product, variant } = entry.row;
    const current = byProduct.get(product.id) ?? {
      product,
      variants: [],
      score: 0,
    };
    current.variants.push(variant);
    current.score = Math.max(current.score, score);
    byProduct.set(product.id, current);
  });

  return [...byProduct.values()]
    .sort(
      (a, b) =>
        b.score - a.score || a.product.name.localeCompare(b.product.name),
    )
    .slice(0, limit)
    .map(({ product, variants }) => ({ product, variants }));
}
